import { type JSX, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';

import { totpVerify } from '../api.js';

/**
 * Second-factor gate shown after a password sign-in when the account has TOTP
 * enabled (the server issued an mfa_pending session). Accepts a 6-digit
 * authenticator code OR a recovery code; on success the App re-fetches /me and
 * lands the user in their fleets.
 */
type View =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'error'; message: string };

export function TwoFactorChallenge(props: {
  onVerified: () => void | Promise<void>;
  /** Bail out (sign out the half-authed session). */
  onCancel: () => void | Promise<void>;
}): JSX.Element {
  const [code, setCode] = useState('');
  const [view, setView] = useState<View>({ kind: 'idle' });

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const trimmed = code.trim();
    if (trimmed.length === 0) return;
    setView({ kind: 'busy' });
    const res = await totpVerify(trimmed);
    if (res.kind === 'ok') {
      await props.onVerified();
      return;
    }
    if (res.kind === 'bad-code') {
      setView({ kind: 'error', message: 'That code is incorrect or expired.' });
      return;
    }
    if (res.kind === 'network') {
      setView({ kind: 'error', message: `Network: ${res.message}` });
      return;
    }
    setView({ kind: 'error', message: `Server returned ${String(res.status)}.` });
  }

  return (
    <Card variant="outlined" data-testid="twofactor-challenge" sx={{ maxWidth: 460, mx: 'auto' }}>
      <CardContent>
        <Typography variant="h6" fontWeight={700}>Enter your authenticator code</Typography>
        <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
          Open your authenticator app and enter the 6-digit code. Lost your device? Enter one of
          your recovery codes instead.
        </Typography>

        <Box component="form" onSubmit={(e) => void handleSubmit(e)}>
          <Stack spacing={2}>
            <TextField
              label="Code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              autoFocus
              required
              fullWidth
              inputProps={{ inputMode: 'text', maxLength: 32, autoComplete: 'one-time-code', 'data-testid': 'twofactor-code' }}
            />
            {view.kind === 'error' && <Alert severity="error" role="alert">{view.message}</Alert>}
            <Stack direction="row" spacing={1}>
              <Button
                type="submit"
                variant="contained"
                disabled={view.kind === 'busy' || code.trim().length === 0}
                data-testid="twofactor-submit"
                sx={{ py: 1 }}
              >
                {view.kind === 'busy' ? 'Verifying…' : 'Verify'}
              </Button>
              <Button onClick={() => void props.onCancel()} disabled={view.kind === 'busy'} data-testid="twofactor-cancel">
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}
